import { cn } from 'lib/utils'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { WeeklyDayBucket, WeeklyQueueItem } from '../../store/weekly-queue-store'
import { DayTile } from './DayTile'

interface WeekStripProps {
  days: WeeklyDayBucket[]
  items: WeeklyQueueItem[]
  selectedDate: string | null
  onSelect: (date: string, source: 'tap' | 'keyboard') => void
  className?: string
}

export function WeekStrip({ days, items, selectedDate, onSelect, className }: WeekStripProps) {
  const itemsById = new Map(items.map(item => [item.creativeId, item]))
  const stripRef = useRef<HTMLDivElement | null>(null)

  // The cockpit window is anchored on today (`days[0]` is today, `days[6]`
  // is today + 6). The trial-track lights the today segment specifically,
  // so we look up its index defensively rather than assuming 0 in case the
  // window anchor ever shifts (e.g. a "this week from Monday" mode lands).
  const todayIndex = useMemo(() => {
    const todayUtc = new Date()
    todayUtc.setUTCHours(0, 0, 0, 0)
    const todayKey = todayUtc.toISOString().slice(0, 10)
    return days.findIndex(d => d.date.slice(0, 10) === todayKey)
  }, [days])

  const handleKey = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (days.length === 0) return
      const currentIndex = days.findIndex(d => d.date === selectedDate)
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        const next = days[Math.min(days.length - 1, currentIndex + 1)]
        if (next && next.date !== selectedDate) onSelect(next.date, 'keyboard')
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        const prev = days[Math.max(0, currentIndex - 1)]
        if (prev && prev.date !== selectedDate) onSelect(prev.date, 'keyboard')
      }
    },
    [days, selectedDate, onSelect]
  )

  // Scroll the selected tile into view when it changes (mobile UX).
  useEffect(() => {
    const strip = stripRef.current
    if (!strip || !selectedDate) return
    const el = strip.querySelector<HTMLButtonElement>(`[data-day="${CSS.escape(selectedDate)}"]`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    }
  }, [selectedDate])

  return (
    // Outer is the scroll container on mobile; on desktop it lays out as a
    // centered flex so the inline-flex inner block sits under the cockpit
    // header at the same horizontal axis as the rest of the content.
    <div
      className={cn(
        'overflow-x-auto scroll-smooth sm:flex sm:justify-center sm:overflow-visible',
        className
      )}
      style={{ scrollbarWidth: 'thin' }}
    >
      {/* `inline-flex` shrinks to content width so the tiles row and the
          trial-track row underneath share the same horizontal extent
          (the track is `w-full` of this column, which equals the tiles
          row width). */}
      <div className="inline-flex flex-col gap-2 px-0.5 pt-1 pb-1">
        {/* Tiles row owns the tablist semantics and the keyboard nav. */}
        <div
          ref={stripRef}
          role="tablist"
          aria-label="Next 7 days"
          onKeyDown={handleKey}
          tabIndex={0}
          className="flex gap-1.5 focus:outline-none sm:gap-2"
        >
          {days.map((day, index) => {
            const dayItems = day.itemIds
              .map(id => itemsById.get(id))
              .filter((it): it is WeeklyQueueItem => Boolean(it))
            return (
              <div key={day.date} data-day={day.date} className="shrink-0">
                <DayTile
                  date={day.date}
                  dayOffset={index}
                  items={dayItems}
                  expectedCount={day.expectedCount}
                  selected={day.date === selectedDate}
                  onSelect={d => onSelect(d, 'tap')}
                />
              </div>
            )
          })}
        </div>

        {/* Trial-track timeline. A 1px hairline along the bottom of the
            strip with an acid-pulse riding the today segment. Borrows the
            paywall's trial-track aesthetic so the cockpit reads as one
            week's narrative arc rather than seven independent buttons.
            Hidden when today isn't in the visible window (defensive). */}
        {todayIndex >= 0 && days.length > 0 && (
          <div className="relative h-[2px] w-full" aria-hidden>
            {/* Base hairline behind everything. Token-mixed so it stays
                visible on both themes — `bg-line-bright` alone vanishes
                against the light cockpit card surface (`#CFD3DA` line on
                `#F2F3F5` bg). 22% of `--paper` keeps the contrast in
                both directions while staying subtle. */}
            <div
              className="absolute inset-0"
              style={{ background: 'color-mix(in srgb, var(--paper) 22%, transparent)' }}
            />
            {/* Today segment frames the acid pulse. `overflow-hidden`
                clips the pulse to the segment so it doesn't bleed into
                neighbouring days. */}
            <div
              className="absolute inset-y-0 overflow-hidden"
              style={{
                left: `${(todayIndex / days.length) * 100}%`,
                width: `${(1 / days.length) * 100}%`,
              }}
            >
              <span className="trial-pulse absolute inset-0" />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
